import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SimpleDialog from '../SimpleDialog/SimpleDialog';
import { InputText } from '../../ui';
import { VerifyCode } from '../../MainEmailForm/MainEmailForm';
import { AppState } from '../../../store/state';
import { AppDispatch } from '../../../store/store';
import userSlice from '../../../store/user/slice';
import { connect } from 'react-redux';
import { isValidEmailFormat } from '../../../helpers/email';
import { UserAlias, UserState } from '../../../store/user/types';
import { selectUser } from '../../../store/selectors';
import { useTranslation } from 'react-i18next';
import { sendVerificationCode } from '../../../store/auth/sagas';
import VerificationCode from '../../VerificationCode/VerificationCode';

interface Props {
  initEmail?: string | null;
  handleClose: () => void;
  postAddUserAliasAction: (data: VerifyCode) => void;
  postAddUserAliasClearAction: () => void;
  userState: UserState;
}

const AddProfileEmailDialog: React.FC<Props> = ({
  initEmail,
  handleClose,
  postAddUserAliasAction,
  postAddUserAliasClearAction,
  userState,
}) => {
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState<string>(initEmail ?? '');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [showCodeVerification, setShowCodeVerification] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const userAliasById: { [key: number]: UserAlias } = userState.data.user_alias_by_id;
  const mergeRequired = !!userState.data.merge_required;

  useEffect(() => {
    return postAddUserAliasClearAction;
  }, []);

  useEffect(() => {
    const user = Object.values(userAliasById).find((userAlias) => userAlias.email === email);
    if (user) {
      handleClose();
    }
  }, [userAliasById]);

  const handleOkFirstStep = async () => {
    setIsLoading(true);
    const res = await sendVerificationCode(email);
    switch (res.type) {
      case 'ok':
        setIsLoading(false);
        setShowCodeVerification(true);
        break;
      case 'validation-error':
        break;
      case 'error':
        break;
    }
  };

  const handleOkSecondStep = () => {
    postAddUserAliasAction({ email, code: verificationCode });
  };

  const isDisabledOkButton =
    isLoading ||
    !isValidEmailFormat(email) ||
    (!!initEmail && !verificationCode) ||
    (showCodeVerification && !verificationCode);

  return (
    <SimpleDialog
      title={t('Add email address')}
      open={true}
      handleClose={handleClose}
      hideCancel={mergeRequired}
      handleOk={mergeRequired ? handleClose : showCodeVerification ? handleOkSecondStep : handleOkFirstStep}
      closeOnOk={false}
      disableOkButton={isDisabledOkButton}
      okButtonText={mergeRequired ? t('OK') : showCodeVerification ? t('Verify code') : t('Continue')}
    >
      <StyledInputWrapper>
        {mergeRequired ? (
          <p className="mb-2">
            <span className="font-bold">{email} </span>
            {t("already exists in the system so we'll proceed to merge it to the current account.")}
            <br />
            {t('You will get a response by email in the next 48 hours.')}
          </p>
        ) : showCodeVerification ? (
          <VerificationCode email={email} onChange={(e) => setVerificationCode(e.target.value)} />
        ) : (
          <InputText
            label={t('Email')}
            required
            defaultValue={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            className="inputWrapper"
          />
        )}
      </StyledInputWrapper>
    </SimpleDialog>
  );
};

const mapStateToProps = (state: AppState) => ({
  userState: selectUser(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  postAddUserAliasAction: (data: VerifyCode) => dispatch(userSlice.actions['POST:ALIAS'](data)),
  postAddUserAliasClearAction: () => dispatch(userSlice.actions['POST:ALIAS:CLEAR']()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddProfileEmailDialog);

const StyledInputWrapper = styled.div`
  .inputWrapper {
    width: 30rem;
    display: flex;
    flex-direction: row;

    label {
      margin-bottom: 0;
      margin-right: 1rem;
      width: 7.3rem;
      align-self: center;
      padding-top: 0;
    }

    .helperText {
      margin-top: 0;
    }
  }
  .warning-message {
    margin-left: 6.7rem;
    max-width: 23rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;
